// import {ListControllerProps, RaRecord} from 'ra-core';
import {
  ColumnDef,
  createColumnHelper,
  getCoreRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import {Dialog, DialogContent, DialogFooter} from '@/shadcn_components/ui/dialog';
import Pagination from 'components/refinedev/shared/Pagination';
import MediaList from './image-list';
import {useEffect, useMemo} from 'react';
import {ImageItemView} from './components';
import {ChoiceProps} from 'hooks/types';
import useCheckList, {UseSelectedList} from '@/hooks//useCheckList';
import formatter from '@/lib/formatter';
import {Button} from '@/shadcn_components/ui/button';
import {Checkbox} from '@/shadcn_components/ui/checkbox';
import {HttpError, useTable} from '@refinedev/core';
import {defaultQueryOptions} from '@/pages/_utils';
import {BaseRecord, useTableReturnType} from '@refinedev/core';
import { UseDialogStateReturn } from '@/hooks/useDialogState';

const thisResource = 'images';

const columnHelper = createColumnHelper<any>();

const columns = MediaList.table_columns;

const ImageSelectPopup = (
  props: Pick<UseDialogStateReturn, 'open' | 'closeDialog'> & {onOk?: (items: ChoiceProps[]) => void},
) => {
  const useTableResults = useTable<any, HttpError>({
    resource: thisResource,
    sorters: {initial: [{field: 'id', order: 'asc'}]},
    queryOptions: {
      ...defaultQueryOptions,
    },
    errorNotification: false,
    meta: {
      append: ['url', 'thumbnails'],
    },
  });

  const checkListState = useCheckList();
  const {setSelectedItems, selectedItems} = checkListState;

  async function onSelectConfirm() {
    console.log('selected', selectedItems);
    props?.onOk?.(selectedItems);
    props.closeDialog();
  }

  /// when open, reset selected items
  useEffect(() => {
    if (props.open) setSelectedItems([]);
  }, [props.open]);

  return (
    <Dialog open={props.open} onOpenChange={props.closeDialog}>
      <DialogContent className="max-w-screen-xl">
        <div className="flex flex-col gap-4 pt-3">
          <Cardgrid columns={columns} {...checkListState} tableResults={useTableResults} />
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            color="red"
            onClick={e => {
              e.stopPropagation();
              props.closeDialog();
            }}
            className="mr-1">
            <span>Hủy bỏ</span>
          </Button>
          <Button
            onClick={e => {
              e.stopPropagation();
              onSelectConfirm();
            }}>
            <span>Chọn</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Cardgrid = <RecordType extends BaseRecord = BaseRecord>({
  meta,
  columns,
  tableResults,
  // listProps,
  // selectedItems,
  toggleItemSelectState,
  isSelected,
}: Pick<UseSelectedList, 'isSelected' | 'toggleItemSelectState'> & {
  meta?: any;
  columns: ColumnDef<RecordType>[];
  tableResults: useTableReturnType;
}) => {
  const data = tableResults.tableQuery.data?.data || [];
  const total = tableResults.tableQuery.data?.total || 0;
  const {current, setCurrent, setSorters, pageSize, sorters} = tableResults;

  const pagination = useMemo(
    () => ({
      pageIndex: current - 1,
      pageSize,
    }),
    [current, pageSize],
  );

  const sorting = useMemo<SortingState>(
    () => [
      {
        id: sorters?.[0]?.field,
        desc: sorters?.[0]?.order === 'desc',
      },
    ],
    [sorters],
  );

  const table = useReactTable({
    data: data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    pageCount: Math.ceil(total / pageSize),
    state: {
      pagination,
      sorting,
    },
    manualPagination: true,
    manualSorting: true,
    meta,
  });

  return (
    <div className="h-full w-full rounded-none">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 p-2">
        {table.getRowModel().rows.map(row => (
          <ImageItemView
            key={row.original.id}
            onClick={() => toggleItemSelectState(row.original)}
            src={
              row.original?.thumbnails?.thumbnail_medium ||
              row.original?.thumbnails?.thumbnail ||
              'no-image'
            }
            alt={row.original.filename}
            header={
              <p className="bg-gray-100">
                #{row.original.id}: {formatter.formatDateTime(row.original?.updated_at)}
              </p>
            }>
            <div className="absolute bottom-0 right-0 p-1">
              <Checkbox
                color="blue"
                checked={isSelected(row.original)}
                onCheckedChange={ch => toggleItemSelectState(row.original, Boolean(ch))}
              />
            </div>
          </ImageItemView>
        ))}
      </div>
      <Pagination table={table} tableResults={tableResults} />
    </div>
  );
};

export default ImageSelectPopup;
